function StatCard({ label, value, tone }) {
  return (
    <div className={`rounded-2xl border ${tone} px-4 py-3 shadow-sm`}>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</p>
      <p className="mt-2 text-2xl font-extrabold text-[#0a2540]">{value}</p>
    </div>
  );
}

function Hero() {
  return (
    <section
      id="home"
      className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.35),_transparent_35%),linear-gradient(135deg,_#eff6ff_0%,_#f8fafc_55%,_#dbeafe_100%)]"
    >
      <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white/80 to-transparent" />
      <div className="mx-auto grid max-w-7xl gap-14 px-6 pb-20 pt-16 lg:grid-cols-[1.05fr_0.95fr] lg:px-8 lg:pb-28 lg:pt-20">
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white/80 px-4 py-2 text-sm font-semibold text-primary shadow-sm backdrop-blur">
            <span className="h-2.5 w-2.5 rounded-full bg-accent" />
            Growth-ready CRM for modern teams
          </div>
          <h1 className="mt-6 max-w-2xl font-display text-5xl font-extrabold leading-tight text-[#0a2540] md:text-5xl">
            Enhance Customer Relationships with Smart CRM
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-8 text-slate-600">
            Centralize pipelines, automate follow-ups, and turn scattered customer data into clear revenue momentum.
            Opt4CRM helps sales, support, and leadership move in sync.
          </p>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <a
              href="#pricing"
              className="inline-flex items-center justify-center rounded-full bg-[#0b5ed7] px-7 py-3.5 text-base font-bold text-white shadow-glow transition hover:-translate-y-0.5 hover:bg-[#0a2540]"
            >
              Get Started
            </a>
            <a
              href="#contact"
              className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-7 py-3.5 text-base font-bold text-[#0a2540] transition hover:-translate-y-0.5 hover:border-[#0b5ed7] hover:text-[#0b5ed7]"
            >
              Book Demo
            </a>
          </div>
          <div className="mt-10 grid max-w-xl gap-4 sm:grid-cols-3">
            <StatCard label="Active Users" value="12K+" tone="border-blue-100 bg-white/90" />
            <StatCard label="Productivity Lift" value="38%" tone="border-cyan-100 bg-cyan-50/80" />
            <StatCard label="Response Time" value="2.1x" tone="border-indigo-100 bg-indigo-50/80" />
          </div>
        </div>

        <div className="relative">
          <div className="absolute -left-8 top-14 h-24 w-24 rounded-full bg-blue-300/30 blur-3xl" />
          <div className="absolute -right-8 bottom-10 h-28 w-28 rounded-full bg-cyan-300/30 blur-3xl" />
          <div className="relative rounded-[32px] border border-white/70 bg-white/85 p-5 shadow-2xl shadow-blue-200/50 backdrop-blur">
            <div className="rounded-[28px] bg-[#0a2540] p-5 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-blue-100">Sales Dashboard</p>
                  <h2 className="mt-1 text-2xl font-bold">Live Pipeline Overview</h2>
                </div>
                <div className="flex gap-2">
                  <span className="h-3 w-3 rounded-full bg-white/30" />
                  <span className="h-3 w-3 rounded-full bg-white/30" />
                  <span className="h-3 w-3 rounded-full bg-white/30" />
                </div>
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-[1.25fr_0.75fr]">
                <div className="rounded-3xl bg-white/10 p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-blue-100">Revenue Forecast</p>
                    <span className="rounded-full bg-emerald-400/20 px-3 py-1 text-xs font-semibold text-emerald-200">
                      +24.8%
                    </span>
                  </div>
                  <div className="mt-5 flex h-40 items-end gap-3">
                    {['35%', '50%', '42%', '68%', '75%', '82%'].map((height, index) => (
                      <div
                        key={height}
                        className={`flex-1 rounded-t-2xl ${
                          index === 5 ? 'bg-gradient-to-t from-[#3b82f6] to-[#93c5fd]' : 'bg-white/20'
                        }`}
                        style={{ height }}
                      />
                    ))}
                  </div>
                  <div className="mt-4 flex items-center justify-between text-xs text-blue-100">
                    <span>Jan</span>
                    <span>Feb</span>
                    <span>Mar</span>
                    <span>Apr</span>
                    <span>May</span>
                    <span>Jun</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-3xl bg-white p-4 text-slate-800">
                    <p className="text-sm font-semibold text-slate-500">Qualified Leads</p>
                    <p className="mt-2 text-3xl font-extrabold text-[#0a2540]">1,284</p>
                    <p className="mt-1 text-sm text-emerald-600">89 new this week</p>
                  </div>
                  <div className="rounded-3xl bg-white/10 p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-blue-100">Task Progress</p>
                      <p className="text-sm font-semibold text-white">76%</p>
                    </div>
                    <div className="mt-3 h-3 rounded-full bg-white/15">
                      <div className="h-3 w-3/4 rounded-full bg-gradient-to-r from-[#67e8f9] to-[#3b82f6]" />
                    </div>
                    <ul className="mt-4 space-y-3 text-sm text-blue-50">
                      <li className="flex items-center justify-between">
                        <span>Lead assignment</span>
                        <span>Done</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span>Email sequence</span>
                        <span>Running</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span>Demo bookings</span>
                        <span>Today</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
